/**
 * useAffineMessaging — Messaging hook for Affine iframe
 *
 * Handles PostMessage events from Affine embed mode:
 *   AFFINE_READY — workspace loaded, fires onReady callback
 *   AFFINE_ROUTE_CHANGE — SPA navigation inside Affine
 *   AFFINE_ERROR — Affine reports an error, fires onError callback
 *
 * Also detects readiness via iframe onLoad + cross-origin SecurityError
 * as a fallback (srcDoc fires onLoad TWICE — form HTML then SSO redirect).
 *
 * Options are stored in a ref to avoid stale closures (CRM GOTCHA G4).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { AFFINE_MESSAGES, AFFINE_ORIGIN } from "./types";

interface UseAffineMessagingOptions {
  onReady?: () => void;
  onError?: (error: string) => void;
}

export function useAffineMessaging(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  options: UseAffineMessagingOptions
) {
  const [isReady, setIsReady] = useState(false);
  const loadCountRef = useRef(0);
  const optionsRef = useRef(options);

  // Keep options ref current to avoid stale closures
  useEffect(() => {
    optionsRef.current = options;
  });

  // Listen for PostMessage from Affine embed mode
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== AFFINE_ORIGIN) return;

      const { type, source, payload } = event.data ?? {};
      // Only handle messages from our embed-mode sender (defense-in-depth)
      if (source !== "affine") return;

      if (type === AFFINE_MESSAGES.READY) {
        setIsReady(true);
        optionsRef.current.onReady?.();
      } else if (type === AFFINE_MESSAGES.ROUTE_CHANGE) {
        if (payload?.path) {
          console.log("[AffineMessaging] Route change:", payload.path);
        }
      } else if (type === AFFINE_MESSAGES.ERROR) {
        // Affine sends { message: string } object, not a raw string
        const errorMsg =
          typeof payload === "string"
            ? payload
            : (payload?.message ?? "Unknown Affine error");
        optionsRef.current.onError?.(String(errorMsg));
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // iframe onLoad handler — detect when Affine has loaded after SSO redirect.
  // srcDoc is same-origin so we CAN read contentWindow.location.
  // After form submits → SSO redirect → Affine loads, the iframe becomes
  // cross-origin and reading location throws SecurityError. That error IS
  // our readiness signal.
  const handleIframeLoad = useCallback(() => {
    loadCountRef.current += 1;
    try {
      const origin = iframeRef.current?.contentWindow?.location?.origin;
      // srcDoc pages have origin "null" (string) or null. If we can read
      // origin without error and it's still srcDoc, not ready yet.
      if (origin === "null" || origin === null) return;
      // If we can read it and it matches AFFINE_ORIGIN, ready.
      if (origin === AFFINE_ORIGIN) {
        setIsReady(true);
        optionsRef.current.onReady?.();
      }
    } catch {
      // SecurityError = cross-origin = iframe navigated to Affine. Ready!
      setIsReady(true);
      optionsRef.current.onReady?.();
    }
  }, []);

  // Reset state for retry flow
  const reset = useCallback(() => {
    setIsReady(false);
    loadCountRef.current = 0;
  }, []);

  return { isReady, handleIframeLoad, reset };
}
